import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { useMsal } from "@azure/msal-react";
import { useMsalAuth } from "../hooks/useMsalAuth";
import { ROUTES } from "./shared/constant";
import SkypointLogoLoader from "./SkypointLogoLander/SkypointLogoLoader";
import { NoAccessScreen } from "./access/NoAccessScreen";
import { denialCodeOf, type AccessDenialCode } from "./access/accessDenial";
import { authService } from "../utils/authService";
import { loadUserPreferences } from "../services/userPreferencesService";
import { createSession } from "../services/authSessionService";
import { appInsightsService } from "../services/appInsights.service";
import { logger } from "../services/logger.service";

function Callback(): React.ReactElement {
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  // Why the user can't get in (Phase U): no roles anywhere, blocked, or a failed check.
  const [denied, setDenied] = useState<AccessDenialCode | null>(null);
  const navigate = useNavigate();
  const { inProgress } = useMsal();
  const { currentUser } = useMsalAuth();
  const hasAttemptedLogin = useRef(false);

  useEffect(() => {
    if (inProgress === "startup" || inProgress === "handleRedirect") {
      return;
    }

    if (hasAttemptedLogin.current) {
      return;
    }

    const user = currentUser;
    if (!user) {
      logger.warn("[Callback] No user found after MSAL processing");
      setErrorMessage("Authentication failed - no user found");
      setIsError(true);
      return;
    }

    hasAttemptedLogin.current = true;
    // Kick both off before awaiting either: the session and the stored
    // preferences are independent, so overlapping them removes a round trip
    // from the login path. loadUserPreferences() resolves to null rather than
    // throwing, so it cannot reject this Promise.all.
    const sessionRequest = createSession();
    const preferencesRequest = loadUserPreferences();

    Promise.all([sessionRequest, preferencesRequest])
      .then(async ([session, preferences]) => {
        // Read saved redirect BEFORE any async calls so nothing can clear it in the meantime
        const savedRedirect = localStorage.getItem("skyhire_redirect_after_login");
        localStorage.removeItem("skyhire_redirect_after_login");

        // Pick instance: preferences > saved > first accessible. A remembered one
        // only while the user can still open it: the server now refuses the rest
        // (deny-by-default), and landing there would be a no-access screen for a
        // user who has access elsewhere.
        const accessible = session.accessibleInstances;
        const remembered = [preferences?.instanceId, authService.getSavedInstanceId()].find(
          (id) => id && accessible.some((option) => option.instanceId === id),
        );
        const instanceId = remembered || accessible[0]?.instanceId;

        if (!instanceId) {
          logger.warn("[Callback] No accessible instances found");
          setDenied("NO_ASSIGNMENT");
          return;
        }

        // Track login event (fire-and-forget)
        const token = await authService.getAccessToken();
        if (token) {
          fetch("/api/v1/activity-logs", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`,
            },
            body: JSON.stringify({
              instanceId,
              actionType: "login",
              category: "auth",
              pagePath: "/callback",
              pageTitle: "Login",
              details: { userId: session.user.id },
            }),
          }).catch(() => {});
          appInsightsService.trackEvent("UserLogin", {
            userId: session.user.id,
            instanceId,
            // Elapsed since the /callback document started loading — i.e. the
            // post-redirect hydration window.
            durationMs: Math.round(performance.now()),
          });
        }

        if (savedRedirect && savedRedirect !== "/" && savedRedirect !== "/callback") {
          const hasInstanceId = savedRedirect.includes("instanceid=");
          if (hasInstanceId) {
            navigate(savedRedirect, { replace: true });
          } else {
            const separator = savedRedirect.includes("?") ? "&" : "?";
            navigate(`${savedRedirect}${separator}instanceid=${instanceId}`, { replace: true });
          }
        } else {
          navigate(`${ROUTES.HOME}?instanceid=${instanceId}`, { replace: true });
        }
      })
      .catch((error) => {
        // A blocked account says so; anything else is a check that didn't complete.
        // Set before logging, so a failing telemetry call cannot swallow the screen.
        setDenied(denialCodeOf(error) ?? "CHECK_FAILED");
        logger.error("[Callback] Session creation error", error);
      });
  }, [navigate, currentUser, inProgress]);

  if (denied) {
    return (
      <NoAccessScreen
        code={denied}
        instances={[]}
      />
    );
  }

  if (isError) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          fontFamily: "Arial, Helvetica, sans-serif",
          textAlign: "center",
          padding: "0 24px",
        }}
      >
        <h3
          className="dark:text-foreground!"
          style={{ margin: 0, fontSize: "18px", fontWeight: 500, color: "#333" }}
        >
          Something went wrong
        </h3>
        <p
          className="dark:text-muted-foreground!"
          style={{ margin: "8px 0 16px", fontSize: "14px", color: "#666" }}
        >
          {errorMessage || "Failed to connect to server"}
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: "8px 24px",
            fontSize: "14px",
            backgroundColor: "#0078d4",
            color: "#fff",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return <SkypointLogoLoader />;
}

export default Callback;
